import { useState, useEffect } from "react";
import axios from "axios";
import { Activity, CheckCircle2, Loader2, PlayCircle } from "lucide-react";
import { API } from "../lib/constants";

const SERIES_NAMES = {
  "the-red-ink-chronicles": "The Red Ink Chronicles",
  "signed-sealed-betrayed": "Signed, Sealed, Betrayed",
  "the-notarys-shadow": "The Notary's Shadow",
};

export default function ProductionStatusWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "series-season" string while starting
  const [feedback, setFeedback] = useState(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/production/status`);
      setData(res.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, []);

  const handleStart = async (series, season) => {
    const key = `${series}-${season}`;
    setBusy(key);
    setFeedback(null);
    try {
      const token = localStorage.getItem("admin_token") || "";
      const headers = token
        ? { Authorization: `Bearer ${token}`, "X-Admin-Token": token }
        : {};
      const res = await axios.post(`${API}/production/start`, { series, season }, { headers });
      setFeedback({ ok: true, msg: `Queued: ${SERIES_NAMES[series] || series} S${season}` });
      // Refresh status soon
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg = detail || "Failed to start production";
      if (status === 403) {
        msg = "Not signed in as admin. Log in with the admin password first.";
      } else if (status === 404) {
        msg = detail || "No episodes found for that season.";
      } else if (status === 502) {
        msg = "TTS job rejected. Check /admin/recovery for details.";
      }
      setFeedback({ ok: false, msg });
    } finally {
      setBusy(null);
      setTimeout(() => setFeedback(null), 6000);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid="production-status-widget">
        <div className="flex items-center gap-2 text-xs text-gray-500 font-mono"><Loader2 size={14} className="animate-spin" /> LOADING PRODUCTION STATUS…</div>
      </div>
    );
  }
  if (!data) return null;

  const inProgress = data.tts_pct < 100 || data.mix_pct < 100;

  return (
    <div className="rounded-lg border border-white/10 bg-gradient-to-br from-[#0a0a0a] to-[#050505] p-5" data-testid="production-status-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {inProgress ? (
            <Activity size={16} className="text-red-500 animate-pulse" />
          ) : (
            <CheckCircle2 size={16} className="text-emerald-400" />
          )}
          <h3 className="font-cinzel text-sm font-bold tracking-wider text-white">PRODUCTION STATUS</h3>
        </div>
        <div className="text-xs text-gray-500 font-mono">
          {data.grand_tts} / {data.grand_total} TTS · {data.grand_mix} / {data.grand_total} MIX
        </div>
      </div>

      {feedback && (
        <div className={`text-xs font-mono mb-3 px-3 py-2 rounded ${feedback.ok ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/15 text-red-300 border border-red-500/30'}`} data-testid="production-feedback">
          {feedback.msg}
        </div>
      )}

      {/* Grand totals */}
      <div className="space-y-2 mb-4">
        <Bar label="TTS" pct={data.tts_pct} color="bg-red-500" />
        <Bar label="MIX" pct={data.mix_pct} color="bg-amber-500" />
      </div>

      {/* Per-series breakdown */}
      <div className="space-y-3" data-testid="production-status-series">
        {data.series.map((s) => (
          <div key={s.series} className="border-t border-white/5 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono tracking-wide text-gray-400">{SERIES_NAMES[s.series] || s.series}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {s.seasons.map((ss) => (
                <SeasonChip
                  key={ss.season}
                  series={s.series}
                  season={ss}
                  busy={busy === `${s.series}-${ss.season}`}
                  onStart={() => handleStart(s.series, ss.season)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bar({ label, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono text-gray-500 mb-1">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SeasonChip({ series, season, busy, onStart }) {
  const both = season.tts_pct === 100 && season.mix_pct === 100;
  const noProgress = season.tts === 0 && season.mix === 0;
  const partial = !both && !noProgress;
  const cls = both
    ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
    : noProgress
    ? "bg-white/5 border-white/10 text-gray-500"
    : "bg-red-500/15 border-red-500/30 text-red-300";
  const tip = `S${season.season}: TTS ${season.tts}/${season.total} · MIX ${season.mix}/${season.total}`;
  return (
    <div
      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono border ${cls}`}
      title={tip}
      data-testid={`season-chip-${series}-${season.season}`}
    >
      <span>S{season.season}</span>
      <span className="opacity-70">{season.tts}/{season.total}·{season.mix}/{season.total}</span>
      {!both && (
        <button
          onClick={onStart}
          disabled={busy}
          title={partial ? "Resume production (skips existing)" : "Start production"}
          className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded hover:bg-white/10 disabled:opacity-50"
          data-testid={`start-production-btn-${series}-${season.season}`}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
        </button>
      )}
    </div>
  );
}

